import moment from 'moment';
import { useInfiniteCreatorBulkUploads } from '@round/creatorbase-helpers/api-hooks';
import { Empty, lucideIcons, Skeleton, VirtualList } from '@round/creatorbase-ui';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import BulkUploadCard from '../BulkUploadCard/BulkUploadCard';
import NextPageStatus from '../NextPageStatus/NextPageStatus';
import styles from './BulkUploads.module.css';

const { AlertTriangle } = lucideIcons;

type Props = {
    onSelectBulkUpload: (bulkUploadId: number) => void;
};

/**
 * How far back the history reaches. The endpoint applies the same bound of its own accord, but the
 * screen states the window it is showing rather than inheriting whichever default answers.
 */
const UPLOAD_HISTORY_DAYS = 30;

const PAGE_SIZE = 25;

/** A row's height plus the room around it, which the row carries itself since the list positions rows. */
const ROW_ESTIMATE_PX = 53;
const NEXT_PAGE_STATUS_ESTIMATE_PX = 64;
const SCROLL_END_THRESHOLD_PX = 200;

const SKELETON_ROW_COUNT = 3;

/** The Results tab: the batches this client has submitted, newest first. */
const BulkUploads = ({ onSelectBulkUpload }: Props) => {
    const { selectedClientId } = useClientSelect();
    const isClientSelected = typeof selectedClientId === 'number';

    const {
        uploads,
        isLoading,
        isFetching,
        isError,
        errorMessage,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
        isFetchNextPageError,
    } = useInfiniteCreatorBulkUploads(
        {
            client_id: selectedClientId,
            date_from: moment().subtract(UPLOAD_HISTORY_DAYS, 'days').format('YYYY-MM-DD'),
            page_size: PAGE_SIZE,
        },
        { enabled: isClientSelected }
    );

    const handleEndReached = () => {
        // Any fetch, not only a next page: asking for one during a background refetch would cancel it.
        if (!hasNextPage || isFetching) {
            return;
        }

        void fetchNextPage();
    };

    // Checked before isLoading/isError: once uploads are showing, a page that fails to load should
    // not take the ones already on screen away with it.
    if (uploads.length) {
        // The next page's spinner or error is the list's last row rather than something under it, so
        // it scrolls with the rows instead of staying pinned below them.
        const hasNextPageStatus = isFetchingNextPage || isFetchNextPageError;

        return (
            <VirtualList
                className={styles.virtualList}
                count={uploads.length + (hasNextPageStatus ? 1 : 0)}
                estimateSize={(index) => (index < uploads.length ? ROW_ESTIMATE_PX : NEXT_PAGE_STATUS_ESTIMATE_PX)}
                onEndReached={handleEndReached}
                endReachedThreshold={SCROLL_END_THRESHOLD_PX}
            >
                {(virtualRow) => {
                    const upload = uploads[virtualRow.index];
                    if (!upload) {
                        return (
                            <div className={styles.row}>
                                <NextPageStatus
                                    isFetching={isFetchingNextPage}
                                    errorMessage={errorMessage}
                                    errorTitle="Couldn't load more uploads"
                                />
                            </div>
                        );
                    }

                    return (
                        <div className={styles.row}>
                            <BulkUploadCard upload={upload} onSelect={() => onSelectBulkUpload(upload.id)} />
                        </div>
                    );
                }}
            </VirtualList>
        );
    }

    // The client is picked for the user once their clients arrive, so a selection that is not there
    // yet is still loading rather than a client without uploads.
    if (isLoading || !isClientSelected) {
        return (
            <div className={styles.list}>
                {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
                    <Skeleton key={index} className={styles.rowSkeleton} />
                ))}
            </div>
        );
    }

    if (isError) {
        return (
            <Empty>
                <Empty.Header>
                    <Empty.Media variant="icon">
                        <AlertTriangle />
                    </Empty.Media>
                    <Empty.Title>Couldn't load bulk uploads</Empty.Title>
                    {errorMessage && <Empty.Description>{errorMessage}</Empty.Description>}
                </Empty.Header>
            </Empty>
        );
    }

    return (
        <Empty>
            <Empty.Header>
                <Empty.Title>No bulk uploads yet</Empty.Title>
                <Empty.Description>
                    Creators added from the Add tab show up here for {UPLOAD_HISTORY_DAYS} days.
                </Empty.Description>
            </Empty.Header>
        </Empty>
    );
};

export default BulkUploads;
